import { Core } from "@faire/design-tokens";
import { BannerVariant } from "@faire/web-api/indigofair/data/BannerVariant";
import { ColorTokenKey } from "@faire/web-api/indigofair/data/ColorTokenKey";
import { IBannerComponent } from "@faire/web-api/indigofair/data/IBannerComponent";
import { IComponent } from "@faire/web-api/indigofair/data/IComponent";
import { IHeaderComponent } from "@faire/web-api/indigofair/data/IHeaderComponent";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { IResponsiveImages } from "@faire/web-api/indigofair/data/IResponsiveImages";
import { ISpacerComponent } from "@faire/web-api/indigofair/data/ISpacerComponent";
import { ITextElement } from "@faire/web-api/indigofair/data/ITextElement";
import { Spacing } from "@faire/web-api/indigofair/data/Spacing";
import { TextJustification } from "@faire/web-api/indigofair/data/TextJustification";
import { TextStyleType } from "@faire/web-api/indigofair/data/TextStyleType";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import React from "react";

import { Context } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Context";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { ComponentLoggingEventParams } from "@faire/retailer/components/SDUI/__internal__/types";
import { SDUIBannerRenderer } from "@faire/retailer/components/SDUI/ComponentRenderers/Banner/SDUIBannerRenderer";
import { SDUIHeaderRenderer } from "@faire/retailer/components/SDUI/ComponentRenderers/Header/SDUIHeaderRenderer";
import { SDUISpacerRenderer } from "@faire/retailer/components/SDUI/ComponentRenderers/Spacer/SDUISpacerRenderer";

export const MultiImageTitleBanner: React.FC = () => {
  const { layoutElement, requestId } = React.useContext(Context);
  const { isMobile } = useViewport();

  const largeImagesMap = generateResponsiveImagesBreakpointMap(
    layoutElement.images ?? []
  );

  const textType = useResponsiveValue({
    mobileAndAbove: TextStyleType.DISPLAY_S_SERIF,
    tabletAndAbove: TextStyleType.DISPLAY_M,
    desktopAndAbove: TextStyleType.DISPLAY_L,
  });

  const bannerComponent = IComponent.build({
    banner_component: IBannerComponent.build({
      style: IBannerComponent.IBannerStyle.build({
        variant: BannerVariant.IMAGE_BANNER_V2,
      }),
      responsive_images: largeImagesMap,
      title: ITextElement.build({
        text: layoutElement.main_text,
        style: ITextElement.ITextElementStyle.build({
          color: getColorTokenKey(
            layoutElement?.main_text_color,
            ColorTokenKey.CORE_TEXT_PRIMARY
          ),
          type: textType,
        }),
      }),
      description: [
        ITextElement.build({
          text: layoutElement.description_text,
          style: ITextElement.ITextElementStyle.build({
            color: getColorTokenKey(
              layoutElement?.description_text_color,
              ColorTokenKey.CORE_TEXT_PRIMARY
            ),
            type: TextStyleType.PARAGRAPH,
          }),
        }),
      ],
    }),
  });

  const spacerComponent = IComponent.build({
    spacer_component: ISpacerComponent.build({
      vertical_spacing: Spacing.R,
      style: ISpacerComponent.ISpacerComponentStyle.build({
        variant: ISpacerComponent.SpacerVariant.DEFAULT,
      }),
    }),
  });

  const headerComponent = IComponent.build({
    header_component: IHeaderComponent.build({
      title: ITextElement.build({
        text: layoutElement.main_text,
        style: ITextElement.ITextElementStyle.build({
          color: ColorTokenKey.CORE_TEXT_PRIMARY,
          type: TextStyleType.DISPLAY_S_SERIF,
          text_justification: TextJustification.CENTER,
        }),
      }),
    }),
  });

  const mobileBannerComponent = IComponent.build({
    banner_component: IBannerComponent.build({
      style: IBannerComponent.IBannerStyle.build({
        variant: BannerVariant.IMAGE_BANNER_V2,
      }),
      images: largeImagesMap.mobile_and_above,
    }),
  });

  const loggingData = ComponentLoggingEventParams.build({
    request_id: requestId,
    component_name: layoutElement.on_click_event_name,
  });

  return (
    <>
      {isMobile ? (
        <>
          <SDUIBannerRenderer
            component={mobileBannerComponent}
            loggingData={loggingData}
          />
          <SDUISpacerRenderer
            component={spacerComponent}
            loggingData={loggingData}
          />
          <SDUIHeaderRenderer
            component={headerComponent}
            loggingData={loggingData}
          />
        </>
      ) : (
        <SDUIBannerRenderer
          component={bannerComponent}
          loggingData={loggingData}
        />
      )}
    </>
  );
};

const generateResponsiveImagesBreakpointMap = (
  images: IImage[]
): IResponsiveImages => {
  return images.reduce((acc, image) => {
    if (image.type === IImage.Type.MOBILE) {
      acc.mobile_and_above.push(image);
    }
    if (image.type === IImage.Type.TABLET) {
      acc.tablet_and_above.push(image);
    }
    if (image.type === IImage.Type.DESKTOP) {
      acc.desktop_and_above.push(image);
    }
    return acc;
  }, IResponsiveImages.build());
};

// Temporary fix to convert hex to ColorTokenKeys until fully migrated to SDUI
const getColorTokenKey = (
  slateToken: string | undefined,
  defaultValue: keyof typeof ColorTokenKey
) => {
  if (slateToken === Core.text.default) {
    return ColorTokenKey.CORE_TEXT_PRIMARY;
  } else if (slateToken === Core.text.inverse) {
    return ColorTokenKey.CORE_TEXT_PRIMARY_INVERSE;
  } else {
    return defaultValue;
  }
};
